export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface ApiRequest {
  readonly method: HttpMethod
  readonly url: string
  readonly headers: Readonly<Record<string, string>>
  readonly body?: string | FormData
  readonly signal?: AbortSignal
  /** 'base64' decodes the response as the base64 of its raw bytes instead of text - the one
   * way a binary response (a receipt photo) survives the string-typed `ApiResponse.body`
   * without the byte loss `Response.text()`'s UTF-8 decoding would cause. */
  readonly responseEncoding?: 'text' | 'base64'
}

export interface ApiResponse {
  readonly status: number
  readonly headers: Readonly<Record<string, string>>
  readonly body: string
}

/** The one seam a Capacitor host replaces; nothing above it knows about `fetch`. */
export type ApiTransport = (request: ApiRequest) => Promise<ApiResponse>

export class TransportError extends Error {
  readonly code = 'client.transport'
  readonly request: ApiRequest
  constructor(request: ApiRequest, cause: unknown) {
    super(`${request.method} ${request.url} failed`, { cause })
    this.name = 'TransportError'
    this.request = request
  }
}

/** The one place in the repository that calls `fetch`. */
export function fetchTransport(fetchImpl: typeof globalThis.fetch = globalThis.fetch): ApiTransport {
  return async (request) => {
    let response: Response
    try {
      response = await fetchImpl(request.url, {
        method: request.method,
        headers: { ...request.headers },
        ...(request.body === undefined ? {} : { body: request.body }),
        ...(request.signal === undefined ? {} : { signal: request.signal }),
        // 'include': the API is same-site but a different subdomain (e.g. taler-api.example.com
        // vs app.example.com), which is still cross-origin to the browser - 'same-origin' would
        // never send the refresh cookie there even though SameSite=Strict permits it.
        credentials: 'include',
      })
    } catch (cause) {
      throw new TransportError(request, cause)
    }
    const headers: Record<string, string> = {}
    response.headers.forEach((value, key) => {
      headers[key.toLowerCase()] = value
    })
    // Only a success body is ever the binary payload itself - an error status is always a
    // small `application/problem+json` document, decoded as text so it stays parseable.
    const body =
      request.responseEncoding === 'base64' && response.ok
        ? arrayBufferToBase64(await response.arrayBuffer())
        : await response.text()
    return { status: response.status, headers, body }
  }
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  const chunkSize = 0x8000
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize))
  }
  return btoa(binary)
}
